import { useEffect, useState, useCallback, Fragment } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import { useTenantPath } from "@/lib/tenantPath";
import { useRole } from "@/lib/roles";
import { Star, ArrowLeft, Pencil, Trash2, Wallet, CheckCircle2, Clock, Calendar, ChevronRight, ChevronDown, RotateCcw } from "lucide-react";

export default function PromoterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tPath = useTenantPath();
  const { canEdit } = useRole();
  const [promoter, setPromoter] = useState(null);
  const [history, setHistory] = useState({ requests: [], events: {} });
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [payables, setPayables] = useState([]);
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadFinance = useCallback(() => {
    return Promise.all([
      api.get(`/promoters/${id}/stats`).catch(() => ({ data: null })),
      api.get(`/promoters/${id}/monthly-finance`).catch(() => ({ data: [] })),
      api.get(`/promoters/${id}/payables`).catch(() => ({ data: [] })),
    ]).then(([s, m, pay]) => { setStats(s.data); setMonthly(m.data || []); setPayables(pay.data || []); });
  }, [id]);

  useEffect(() => {
    Promise.all([
      api.get(`/promoters/${id}`),
      api.get(`/promoters/${id}/history`),
      api.get(`/promoters/${id}/stats`).catch(() => ({ data: null })),
      api.get(`/promoters/${id}/monthly-finance`).catch(() => ({ data: [] })),
      api.get(`/promoters/${id}/payables`).catch(() => ({ data: [] })),
    ])
      .then(([p, h, s, m, pay]) => { setPromoter(p.data); setHistory(h.data); setStats(s.data); setMonthly(m.data || []); setPayables(pay.data || []); })
      .finally(() => setLoading(false));
  }, [id]);

  const toggleMonth = (month) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(month)) next.delete(month); else next.add(month);
      return next;
    });
  };

  const payEvent = async (ev) => {
    setBusy(true);
    try {
      await api.post(`/promoters/${id}/pay-event`, { event_id: ev.event_id, shift_id: ev.shift_id || null });
      toast.success("Pagamento registado", { description: `${ev.event_name} marcado como pago.` });
      await loadFinance();
    } catch (e) {
      toast.error("Erro ao marcar pago", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const unpayEvent = async (ev) => {
    if (!ev.invoice_id) return;
    setBusy(true);
    try {
      await api.post(`/promoters/${id}/unpay-event?invoice_id=${encodeURIComponent(ev.invoice_id)}`);
      toast.success("Pagamento revertido");
      await loadFinance();
    } catch (e) {
      toast.error("Erro ao reverter", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const toggleReceipt = async (ev) => {
    if (!ev.invoice_id) return;
    setBusy(true);
    try {
      await api.post(`/invoices/${ev.invoice_id}/set-receipt-issued`, { issued: !ev.receipt_issued });
      toast.success(ev.receipt_issued ? "Marcado como sem recibo" : "Recibo marcado como emitido");
      await loadFinance();
    } catch (e) {
      toast.error("Erro", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const payMonth = async (month) => {
    if (!window.confirm(`Marcar TODOS os eventos de ${month} como pagos?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/promoters/${id}/pay-month`, { month });
      toast.success("Mês pago", { description: `${(r.data?.marked_paid || 0) + (r.data?.created_paid || 0)} evento(s) marcados como pagos.` });
      await loadFinance();
    } catch (e) {
      toast.error("Erro ao marcar mês", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Eliminar este promotor?")) return;
    await api.delete(`/promoters/${id}`);
    navigate(tPath("/promotores"));
  };

  if (loading) return <div className="text-sm text-zinc-500">A carregar…</div>;
  if (!promoter) return <div>Não encontrado</div>;

  return (
    <div className="space-y-6" data-testid="promoter-detail-page">
      <Link to={tPath("/promotores")} className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900">
        <ArrowLeft className="w-4 h-4" /> Voltar a promotores
      </Link>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 flex flex-col sm:flex-row gap-6">
        {promoter.photo_url ? (
          <img src={promoter.photo_url} alt={promoter.name} className="w-24 h-24 rounded-2xl object-cover bg-zinc-100" />
        ) : (
          <div className="w-24 h-24 rounded-2xl bg-zinc-200 text-zinc-700 text-2xl font-semibold flex items-center justify-center">
            {promoter.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-zinc-950">{promoter.name}</h1>
              <div className="text-sm text-zinc-500 mt-0.5">{promoter.email} · {promoter.phone}</div>
              <div className="text-sm text-zinc-700 mt-1">{promoter.location || "—"}</div>
            </div>
            <div className="flex gap-2">
              {canEdit && (
                <>
                  <Link to={tPath(`/promotores/${id}/editar`)} data-testid="edit-promoter-button" className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-200 rounded-lg text-sm hover:bg-zinc-50">
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </Link>
                  <button onClick={handleDelete} data-testid="delete-promoter-button" className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-700 rounded-lg text-sm hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(promoter.tags || []).map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-wide font-medium text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-full">{t}</span>
            ))}
            {!promoter.active && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">inativo</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Ranking" value={
          <span className="inline-flex items-center gap-1.5">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {promoter.ranking?.toFixed(1)}
          </span>
        } />
        <Stat label="Taxa de aceitação" value={`${Math.round(promoter.accept_rate)}%`} />
        <Stat label="Taxa de resposta" value={`${Math.round(promoter.response_rate)}%`} />
        <Stat label="Eventos" value={promoter.events_count} />
      </div>

      {promoter.notes && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Notas</div>
          <div className="text-sm text-zinc-700 whitespace-pre-wrap">{promoter.notes}</div>
        </div>
      )}

      {stats && (
        <div data-testid="financial-stats">
          <h2 className="text-xl font-display font-bold text-zinc-950 mb-3">Estatísticas financeiras</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Total a pagar" value={`€${stats.total_invoiced.toFixed(2)}`} icon={Wallet} />
            <Stat label="A pagar (concluídos)" value={`€${(stats.total_receivable || 0).toFixed(2)}`} icon={Clock} accent="text-blue-700" />
            <Stat label="Pago" value={`€${stats.total_paid.toFixed(2)}`} icon={CheckCircle2} accent="text-emerald-700" />
            <Stat label="Pendente" value={`€${stats.total_pending.toFixed(2)}`} icon={Clock} accent="text-amber-700" />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center gap-3">
              <Calendar className="w-5 h-5 text-zinc-400" />
              <div>
                <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Eventos concluídos</div>
                <div className="text-lg font-bold text-zinc-950">{stats.events_done}</div>
              </div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center gap-3">
              <Calendar className="w-5 h-5 text-zinc-400" />
              <div>
                <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Próximos confirmados</div>
                <div className="text-lg font-bold text-zinc-950">{stats.events_upcoming}</div>
              </div>
            </div>
          </div>
          {stats.last_event && (
            <div className="mt-4 bg-white border border-zinc-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Último evento confirmado</div>
              <Link to={tPath(`/eventos/${stats.last_event.id}`)} className="text-sm font-medium text-zinc-900 hover:underline">{stats.last_event.name}</Link>
              <div className="text-xs text-zinc-500">{stats.last_event.date} · {stats.last_event.location}</div>
            </div>
          )}
        </div>
      )}

      {monthly.length > 0 && (
        <div data-testid="promoter-monthly-finance">
          <h2 className="text-xl font-display font-bold text-zinc-950 mb-3">Financeiro mensal</h2>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/50 text-xs font-semibold text-zinc-500 uppercase tracking-wider border-b border-zinc-200">
                <tr>
                  <th className="text-left py-2.5 px-4">Mês (do evento)</th>
                  <th className="text-right py-2.5 px-4">Recibos</th>
                  <th className="text-right py-2.5 px-4">Total a pagar</th>
                  <th className="text-right py-2.5 px-4">A pagar</th>
                  <th className="text-right py-2.5 px-4">Pago</th>
                  <th className="text-right py-2.5 px-4">Pendente</th>
                </tr>
              </thead>
              <tbody>
                {monthly.map((m) => {
                  const isOpen = expandedMonths.has(m.month);
                  const events = payables.filter((p) => p.month === m.month);
                  return (
                    <Fragment key={m.month}>
                      <tr
                        data-testid={`monthly-row-${m.month}`}
                        onClick={() => toggleMonth(m.month)}
                        className="border-b border-zinc-100 cursor-pointer hover:bg-zinc-50"
                      >
                        <td className="py-2.5 px-4 font-medium text-zinc-900">
                          <span className="inline-flex items-center gap-1.5">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />}
                            {m.month}
                          </span>
                        </td>
                        <td className="py-2.5 px-4 text-right text-zinc-700">{m.count}</td>
                        <td className="py-2.5 px-4 text-right font-semibold text-zinc-950">€{m.invoiced.toFixed(2)}</td>
                        <td className="py-2.5 px-4 text-right text-blue-700">€{(m.receivable || 0).toFixed(2)}</td>
                        <td className="py-2.5 px-4 text-right text-emerald-700">€{m.paid.toFixed(2)}</td>
                        <td className="py-2.5 px-4 text-right text-amber-700">
                          <div className="flex items-center justify-end gap-2">
                            <span>€{m.pending.toFixed(2)}</span>
                            {canEdit && events.some((e) => !e.paid) && (
                              <button
                                data-testid={`pay-month-${m.month}`}
                                onClick={(e) => { e.stopPropagation(); payMonth(m.month); }}
                                disabled={busy}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
                                title="Marcar todos os eventos deste mês como pagos"
                              >
                                <CheckCircle2 className="w-3 h-3" /> Pagar mês
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid={`monthly-detail-${m.month}`} className="border-b border-zinc-100 bg-zinc-50/40">
                          <td colSpan={6} className="px-4 py-3">
                            {events.length === 0 ? (
                              <div className="text-xs text-zinc-500 py-2">Sem eventos neste mês.</div>
                            ) : (
                              <div className="divide-y divide-zinc-100">
                                {events.map((ev, idx) => (
                                  <div key={`${ev.event_id}-${ev.shift_id || "x"}-${idx}`} data-testid={`payable-item-${ev.event_id}`} className="flex items-center justify-between gap-3 py-2">
                                    <div className="min-w-0 flex-1">
                                      <Link to={tPath(`/eventos/${ev.event_id}`)} className="text-sm font-medium text-zinc-900 hover:underline truncate block">
                                        {ev.event_name || "Evento"}{ev.shift_name ? ` · ${ev.shift_name}` : ""}
                                      </Link>
                                      <div className="text-xs text-zinc-500">{ev.event_date}{ev.invoice_number && ev.receipt_issued ? ` · Recibo ${ev.invoice_number}` : ""}</div>
                                    </div>
                                    <PayableStatus paid={ev.paid} receiptIssued={ev.receipt_issued} hasInvoice={!!ev.invoice_id} />
                                    <div className="text-sm font-semibold text-zinc-950 w-16 text-right">€{(ev.amount || 0).toFixed(2)}</div>
                                    {canEdit && (
                                      <div className="flex items-center gap-1.5 w-40 justify-end" onClick={(e) => e.stopPropagation()}>
                                        {!ev.paid ? (
                                          <button data-testid={`pay-event-${ev.event_id}`} onClick={() => payEvent(ev)} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60">
                                            <CheckCircle2 className="w-3 h-3" /> Pagar
                                          </button>
                                        ) : (
                                          <>
                                            {!ev.receipt_issued && (
                                              <button data-testid={`mark-receipt-${ev.event_id}`} onClick={() => toggleReceipt(ev)} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-60" title="Marcar que o recibo foi emitido">
                                                Recibo emitido
                                              </button>
                                            )}
                                            <button data-testid={`unpay-event-${ev.event_id}`} onClick={() => unpayEvent(ev)} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-60">
                                              <RotateCcw className="w-3 h-3" /> Reabrir
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-xl font-display font-bold text-zinc-950 mb-3">Histórico</h2>
        <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100">
          {history.requests.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-zinc-500">Sem histórico de pedidos.</div>
          )}
          {history.requests.map((r) => {
            const evt = history.events[r.event_id];
            return (
              <div key={r.id} className="flex items-center justify-between px-5 py-3">
                <div className="min-w-0">
                  <Link to={tPath(`/eventos/${r.event_id}`)} className="text-sm font-medium text-zinc-900 truncate hover:underline">
                    {evt ? evt.name : "Evento removido"}
                  </Link>
                  <div className="text-xs text-zinc-500">Enviado em {new Date(r.sent_at).toLocaleDateString("pt-PT")}</div>
                </div>
                <StateBadge status={r.status} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const PAYABLE_STATUS = {
  pago: { label: "Recibo pago", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pago_sem_recibo: { label: "Pago (sem recibo)", cls: "bg-emerald-50 text-emerald-700 border-emerald-300 border-dashed" },
  pendente: { label: "Recibo pendente", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  sem_recibo: { label: "Sem recibo (a pagar)", cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

function PayableStatus({ paid, receiptIssued, hasInvoice }) {
  let key;
  if (paid && receiptIssued) key = "pago";
  else if (paid && !receiptIssued) key = "pago_sem_recibo";
  else if (!paid && hasInvoice && receiptIssued) key = "pendente";
  else key = "sem_recibo";
  const cfg = PAYABLE_STATUS[key];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border whitespace-nowrap ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${accent || "text-zinc-400"}`} />}
      </div>
      <div className={`text-2xl font-bold tracking-tight mt-2 font-display ${accent || "text-zinc-950"}`}>{value}</div>
    </div>
  );
}
